import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import './OrdersList.css';
import { Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { formatMoney, combinedAmount } from '../../utils/formatMoney';

const adminUser = () => localStorage.getItem('admin') === 'true';

const isIkeaMultiPackageSku = (id) => /^s/i.test(String(id || '').trim());

const lineHasResolvedDimensions = (item) =>
    Array.isArray(item.dimensions) &&
    item.dimensions.length > 0 &&
    item.dimensions.some(
        (d) => Array.isArray(d.packages) && d.packages.length > 0,
    );

/** Order contains at least one s-prefixed line still missing usable dimensions rows. */
const orderNeedsIkeaPackages = (order) =>
    Array.isArray(order.cart) &&
    order.cart.some((item) => isIkeaMultiPackageSku(item?.id) && !lineHasResolvedDimensions(item));

const getV2OrderStatusClass = (status) => {
    switch (status?.toUpperCase()) {
        case 'SUBMITTED':
            return 'ordersv2-status--submitted';
        case 'APPROVED':
            return 'ordersv2-status--approved';
        case 'DECLINED':
            return 'ordersv2-status--declined';
        case 'SHIPPED':
            return 'ordersv2-status--shipped';
        default:
            return '';
    }
};

const OrdersList = ({
    data,
    invoices = false,
    selectMode = false,
    select,
    selectedOrder = undefined,
    onReload,
    embed = false,
}) => {
    const navigate = useNavigate();
    const [resolvingId, setResolvingId] = useState(null);

    const showIkeaMp = adminUser() && !invoices && !selectMode;

    const dataToUse = invoices
        ? [...data].sort((a, b) => {
              const t = (row) => {
                  const d = row.invoice?.date || row.date || row.createdAt;
                  const x = d ? new Date(d).getTime() : 0;
                  return Number.isFinite(x) ? x : 0;
              };
              const tb = t(b);
              const ta = t(a);
              if (tb !== ta) return tb - ta;
              return Number(b.invoice?.invoiceNumber || 0) - Number(a.invoice?.invoiceNumber || 0);
          })
        : data;

    const resolveOneLine = async (articleId) => {
        const res = await fetch(`${process.env.REACT_APP_API}/api/resolveIkeaPackages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ articleId: String(articleId).trim() }),
        });

        let body;
        try {
            body = await res.json();
        } catch {
            body = null;
        }

        if (!res.ok || !body?.dimensions?.length) {
            const msg =
                !res.ok &&
                typeof body?.error === 'string'
                    ? body.error
                    : 'Could not resolve IKEA packages.';
            throw new Error(msg);
        }

        return body.dimensions;
    };

    const persistCart = async (orderId, cart) => {
        const res = await fetch(`${process.env.REACT_APP_API}/api/changeCart/${orderId}`, {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cart }),
        });

        if (res.status !== 200) {
            let msg = 'Could not save updated cart.';
            try {
                const body = await res.json();
                if (typeof body === 'string') msg = body;
                else if (body?.message) msg = body.message;
            } catch {
                /* ignore */
            }
            throw new Error(msg);
        }
    };

    const handleResolveIkea = async (e, order) => {
        e.preventDefault();
        e.stopPropagation();

        if (!Array.isArray(order.cart) || !order._id) return;

        setResolvingId(String(order._id));

        try {
            const cart = [...order.cart];
            let changed = false;

            for (let i = 0; i < cart.length; i++) {
                const line = cart[i];
                if (!line || !isIkeaMultiPackageSku(line.id)) continue;

                if (lineHasResolvedDimensions(line)) continue;

                await new Promise((r) => setTimeout(r, i > 0 ? 500 : 0));

                try {
                    const dimensions = await resolveOneLine(line.id);
                    cart[i] = { ...line, dimensions };
                    changed = true;
                } catch (err) {
                    console.warn('resolve line', line.id, err);
                    toast.error(`${line.name || line.id}: ${err.message}`);
                }
            }

            if (changed) {
                await persistCart(order._id, cart);
                toast.success(`IKEA packages saved for order #${order.orderNumber ?? order.id}.`);
                onReload?.();
            } else {
                toast.info('No multi-pack lines needed resolving (or URLs missing).');
            }
        } catch (err) {
            toast.error(err.message || 'Could not resolve IKEA packages.');
        } finally {
            setResolvingId(null);
        }
    };

    const table = (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {showIkeaMp && <TableCell>IKEA</TableCell>}
                            <TableCell>{invoices ? 'Invoice' : 'Order'} #</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Shipping</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Total</TableCell>
                            {showIkeaMp && <TableCell />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataToUse.map((row) => (
                            <TableRow
                                key={row._id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                className={row._id === selectedOrder ? 'orders-row orders-row-selected' : 'orders-row'}
                                onClick={() => {
                                    if (selectMode) {
                                        select(row._id);
                                    } else {
                                        navigate(`/${invoices ? 'invoice' : 'order'}/${row._id}`);
                                    }
                                }}
                            >
                                {showIkeaMp && (
                                    <TableCell scope="cell" sx={{ verticalAlign: 'middle' }}>
                                        {orderNeedsIkeaPackages(row) ? (
                                            <span className="orders-mp-badge" title="Needs multi-pack resolution">
                                                MP
                                            </span>
                                        ) : orderHasMpLines(row) ? (
                                            <span className="orders-mp-badge orders-mp-done" title="IKEA bundles present">
                                                MP ✓
                                            </span>
                                        ) : (
                                            <span />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell component="th" scope="row">
                                    <p>{invoices ? row.invoice.invoiceNumber : row.orderNumber || row.id}</p>
                                </TableCell>
                                <TableCell>
                                    <p>
                                        {new Intl.DateTimeFormat('en-GB', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        }).format(new Date(row.date || row.createdAt))}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    <div className="orders-shipping">
                                        <p>{row.deliveryname || row.deliveryName}</p>
                                        <p>{row.deliverystreet || row.deliveryStreet}</p>
                                        <p>{row.deliverycity || row.deliveryCity}</p>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {selectMode && !invoices ? (
                                        <span
                                            className={`ordersv2-status ${getV2OrderStatusClass(row.status)}`}
                                        >
                                            {row.status}
                                        </span>
                                    ) : (
                                        <p>{invoices ? row.invoice.status : row.status}</p>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <p>€{formatMoney(combinedAmount(row))}</p>
                                </TableCell>
                                {showIkeaMp && (
                                    <TableCell scope="cell" sx={{ verticalAlign: 'middle' }}>
                                        {Array.isArray(row.cart) && row.cart.some((l) => isIkeaMultiPackageSku(l?.id)) && (
                                                <button
                                                    type="button"
                                                    className="orders-resolve-ikea"
                                                    disabled={resolvingId === String(row._id)}
                                                    title="Resolve component packages from IKEA (server-side)."
                                                    onClick={(e) => handleResolveIkea(e, row)}
                                                >
                                                    {resolvingId === String(row._id) ? 'Resolving…' : 'Resolve MP'}
                                                </button>
                                            )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
    );

    if (embed) {
        return <div className="v2-mui-scope orderslist-embed">{table}</div>;
    }

    return (
        <Container maxWidth="lg" className="mt-20">
            {table}
        </Container>
    );
};

function orderHasMpLines(order) {
    return Array.isArray(order.cart) && order.cart.some((l) => isIkeaMultiPackageSku(l?.id));
}

export default OrdersList;
